import { useQuery } from '@tanstack/react-query';
import { ChevronDownIcon, Loader2Icon } from 'lucide-react';
import { useId, useState, type JSX } from 'react';
import { Button } from '@/components/ui/button.js';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.js';
import { GenerationEvidence } from './GenerationEvidence.js';
import {
  runProposalsQueryOptions,
  runLogsQueryOptions,
} from '@/lib/api/quality.js';

import {
  GenerationLogLines,
  presentLogLine,
  type RunLogVariant,
} from './generation-log-lines.js';

/** The live feed's wording per run kind. */
const ACTIVITY_LABELS: Record<
  RunLogVariant,
  { title: string; waiting: string; ariaLabel: string }
> = {
  generation: {
    title: 'Generating…',
    waiting: 'Waiting for the generator to start…',
    ariaLabel: 'Generation activity',
  },
  exploratory: {
    title: 'Exploring…',
    waiting: 'Waiting for the explorer to start…',
    ariaLabel: 'Exploration activity',
  },
};

/**
 * Live activity feed for a generation or exploratory run that's still working. Seeds from the run's
 * log tail and refreshes as the SSE `log.appended` event invalidates the logs query, so the user sees
 * what the agent is doing right now ("Read requirements.pdf", "Opened https://…") instead of only a
 * spinner. Auto-scrolls to the newest line.
 */
export function GenerationActivity({
  runId,
  variant = 'generation',
}: {
  runId: string;
  variant?: RunLogVariant;
}): JSX.Element {
  const [technicalOpen, setTechnicalOpen] = useState(false);
  const technicalId = useId();
  const { data, isLoading } = useQuery(runLogsQueryOptions(runId));
  const { data: proposals = [] } = useQuery({
    ...runProposalsQueryOptions(runId),
    enabled: variant === 'generation',
    refetchInterval: 3000,
  });
  const current = proposals
    .filter((proposal) => proposal.status === 'proposed')
    .reduce<(typeof proposals)[number] | undefined>(
      (latest, proposal) =>
        !latest || proposal.version > latest.version ? proposal : latest,
      undefined,
    );
  const lines = data ?? [];
  const activity = lines.filter((line) => presentLogLine(line).isActivity);
  const labels = ACTIVITY_LABELS[variant];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Loader2Icon
            className="size-4 animate-spin text-muted-foreground"
            aria-hidden
          />
          {labels.title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {variant === 'generation' &&
        current &&
        (current.generationMeta?.frozenInput ||
          current.generationMeta?.sourceReceipts?.length) ? (
          <div className="mb-3" aria-live="polite">
            <GenerationEvidence
              runId={runId}
              proposalId={current.id}
              version={current.version}
              sources={current.generationMeta?.sourceReceipts}
            />
          </div>
        ) : null}
        {activity.length === 0 ? (
          <p role="status" className="text-sm text-muted-foreground">
            {isLoading
              ? 'Loading activity…'
              : lines.length
                ? 'Waiting for a progress update…'
                : labels.waiting}
          </p>
        ) : (
          <div
            role="log"
            aria-label={labels.ariaLabel}
            aria-live="polite"
            className="space-y-1 text-sm [overflow-wrap:anywhere]"
          >
            {activity.slice(-6).map((line) => (
              <p key={line.n}>{presentLogLine(line).text}</p>
            ))}
          </div>
        )}
        {lines.length > 0 && (
          <div className="mt-3 border-t pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="px-0 text-xs text-muted-foreground"
              aria-expanded={technicalOpen}
              aria-controls={technicalOpen ? technicalId : undefined}
              onClick={() => setTechnicalOpen((open) => !open)}
            >
              <ChevronDownIcon
                className={technicalOpen ? 'size-3.5 rotate-180' : 'size-3.5'}
                aria-hidden
              />
              Technical details
            </Button>
            {technicalOpen && (
              <div id={technicalId} className="pt-2">
                <GenerationLogLines
                  lines={lines}
                  ariaLabel={`${labels.ariaLabel} technical details`}
                  live
                />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
